import React, { useEffect, useState } from "react";
import {
  CirclePlus,
  MoreVertical,
  Edit,
  Trash2,
  ClipboardList,
} from "lucide-react";
import Swal from "sweetalert2";
import EmployeeUpload from "./EmployeeUpload";
import api from "../../../api/axios";

const AddNewEmployee = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("weekly");
  const [search, setSearch] = useState("");

  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    empcode: "",
    dob: "",
    department: "",
    shiftStart: "",
    shiftEnd: "",
    outlet: "",
    dateOfJoining: "",
    designation: "",
  });

  // input handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // fetch employees
  const fetchEmployees = async () => {
    try {
      const res = await api.get("/hrms/employees");
      setEmployees(res.data.data || []);
    } catch (err) {
      Swal.fire("Error", "Failed to fetch employees", "error");
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  // add employee
  const handleAddEmployee = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!formData.firstname || !formData.lastname || !formData.email) {
      Swal.fire("Warning", "Please fill all required fields", "warning");
      setLoading(false);
      return;
    }

    const newEmployee = {
      first_name: formData.firstname,
      last_name: formData.lastname,
      email: formData.email,
      employee_code: formData.empcode,
      department: formData.department,
      dob: formData.dob,
      shift_start_time: formData.shiftStart,
      shift_end_time: formData.shiftEnd,
      outlet: formData.outlet,
      date_of_joining: formData.dateOfJoining,
      designation: formData.designation,
    };

    try {
      const res = await api.post("/hrms/employees", newEmployee);
      if (res.status === 200 || res.status === 201) {
        Swal.fire("Success", "Employee added successfully", "success");
        setEmployees((prev) => [...prev, res.data.data]); // add correct DB data
      }
    } catch {
      Swal.fire("Error", "Failed to add employee", "error");
    } finally {
      setLoading(false);
    }

    // clear form
    setFormData({
      firstname: "",
      lastname: "",
      email: "",
      dob: "",
      empcode: "",
      department: "",
      shiftStart: "",
      shiftEnd: "",
      outlet: "",
      dateOfJoining: "",
      designation: "",
    });
  };

  // remove employee
  const handleRemove = async (id) => {
    const ok = await Swal.fire({
      title: "Remove Employee?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, remove",
    });

    if (!ok.isConfirmed) return;

    try {
      await api.delete(`/hrms/employees/${id}`);

      setEmployees((prev) => prev.filter((x) => x.id !== id));

      Swal.fire("Removed", "Employee deleted", "success");
    } catch {
      Swal.fire("Error", "Failed to remove employee", "error");
    }
  };

  const formatTime = (t) => {
    if (!t) return "-";
    return new Date(`1970-01-01T${t}`).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filtered = employees.filter((x) =>
    `${x.first_name} ${x.last_name} ${x.email}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
      <div className="d-flex align-items-center gap-2 mb-4">
        <ClipboardList size={35} stroke="#302867" />
        <div className="d-flex flex-column">
          <h5 className="fs-4 fw-semibold m-0">Employee Management</h5>
          <span className="text-muted fs-6">
            View employee Details or upload Bulk employee.
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="duty-roster-tabs">
        <button
          className={`duty-roster-tab-btn ${
            activeTab === "weekly" ? "active" : ""
          }`}
          onClick={() => setActiveTab("weekly")}
        >
          Employee Details
        </button>

        <button
          className={`duty-roster-tab-btn ${
            activeTab === "upload" ? "active" : ""
          }`}
          onClick={() => setActiveTab("upload")}
        >
          Upload Bulk Employee
        </button>
      </div>

      {/* WEEKLY TAB */}
      {activeTab === "weekly" && (
        <>
          {/* Add Employee Form */}
          <div className="add-new-emp-container mt-4">
            <h5 className="fs-4 fw-semibold">Add New Employee</h5>
            <form className="mt-4" onSubmit={handleAddEmployee}>
              <div className="row row-gap-3">
                {/* First Name */}
                <div className="col-lg-3 col-md-6 col-12">
                  <label className="form-label fw-medium">First Name</label>
                  <input
                    type="text"
                    name="firstname"
                    className="form-control"
                    value={formData.firstname}
                    onChange={handleChange}
                    placeholder="e.g., John"
                    style={{ backgroundColor: "#f9f9fa" }}
                  />
                </div>

                {/* Last Name */}
                <div className="col-lg-3 col-md-6 col-12">
                  <label className="form-label fw-medium">Last Name</label>
                  <input
                    type="text"
                    name="lastname"
                    className="form-control"
                    value={formData.lastname}
                    onChange={handleChange}
                    placeholder="e.g., Doe"
                    style={{ backgroundColor: "#f9f9fa" }}
                  />
                </div>

                {/* Email */}
                <div className="col-lg-3 col-md-6 col-12">
                  <label className="form-label fw-medium">Email</label>
                  <input
                    type="email"
                    name="email"
                    className="form-control"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="e.g., john.d@example.com"
                    style={{ backgroundColor: "#f9f9fa" }}
                  />
                </div>

                {/* Department */}
                <div className="col-lg-3 col-md-6 col-12">
                  <label className="form-label fw-medium">Department</label>
                  <select
                    name="department"
                    className="form-select"
                    value={formData.department}
                    onChange={handleChange}
                    style={{ backgroundColor: "#f9f9fa" }}
                  >
                    <option value="">Select Department</option>
                    <option value="Housekeeping">Housekeeping</option>
                    <option value="Front Desk">Front Desk</option>
                    <option value="Engineering">Engineering</option>
                    <option value="Kitchen">Kitchen</option>
                    <option value="Security">Security</option>
                    <option value="Sales">Sales</option>
                  </select>
                </div>

                {/* Employee Code */}
                <div className="col-lg-3 col-md-6 col-12">
                  <label className="form-label fw-medium">Employee Code</label>
                  <input
                    type="text"
                    name="empcode"
                    className="form-control"
                    value={formData.empcode}
                    onChange={handleChange}
                    placeholder="e.g., EMP123"
                    style={{ backgroundColor: "#f9f9fa" }}
                  />
                </div>
                <div className="col-lg-3 col-md-6 col-12">
                  <label className="form-label fw-medium">Date of Birth</label>
                  <input
                    type="date"
                    name="dob"
                    className="form-control"
                    value={formData.dob}
                    onChange={handleChange}
                    style={{ backgroundColor: "#f9f9fa" }}
                  />
                </div>

                {/* Designation */}
                <div className="col-lg-3 col-md-6 col-12">
                  <label className="form-label fw-medium">Designation</label>
                  <input
                    type="text"
                    name="designation"
                    className="form-control"
                    value={formData.designation}
                    onChange={handleChange}
                    placeholder="Senior Engineer"
                    style={{ backgroundColor: "#f9f9fa" }}
                  />
                </div>

                {/* Outlet */}
                <div className="col-lg-3 col-md-6 col-12">
                  <label className="form-label fw-medium">Outlet</label>
                  <input
                    type="text"
                    name="outlet"
                    className="form-control"
                    value={formData.outlet}
                    onChange={handleChange}
                    placeholder="Main Building"
                    style={{ backgroundColor: "#f9f9fa" }}
                  />
                </div>

                {/* Date of Joining */}
                <div className="col-lg-3 col-md-6 col-12">
                  <label className="form-label fw-medium">
                    Date of Joining
                  </label>
                  <input
                    type="date"
                    name="dateOfJoining"
                    className="form-control"
                    value={formData.dateOfJoining}
                    onChange={handleChange}
                    style={{ backgroundColor: "#f9f9fa" }}
                  />
                </div>

                {/* Shift Start */}
                <div className="col-lg-3 col-md-6 col-12">
                  <label className="form-label fw-medium">
                    Shift Start Time
                  </label>
                  <input
                    type="time"
                    name="shiftStart"
                    className="form-control"
                    value={formData.shiftStart}
                    onChange={handleChange}
                    style={{ backgroundColor: "#f9f9fa" }}
                  />
                </div>

                {/* Shift End */}
                <div className="col-lg-3 col-md-6 col-12">
                  <label className="form-label fw-medium">Shift End Time</label>
                  <input
                    type="time"
                    name="shiftEnd"
                    className="form-control"
                    value={formData.shiftEnd}
                    onChange={handleChange}
                    style={{ backgroundColor: "#f9f9fa" }}
                  />
                </div>
              </div>

              <button
                type="submit"
                className="btn add-emp-btn d-flex align-items-center gap-2 py-2 px-3 mt-4"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm"></span>
                    Adding...
                  </>
                ) : (
                  <>
                    <CirclePlus size={18} />
                    Add Employee
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Employee List */}
          <div className="emp-list-container mt-4">
            <h5 className="fs-4 fw-semibold m-0">Employee List</h5>

            <input
              type="text"
              placeholder="Search employees..."
              className="form-control mt-2"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <div className="table-responsive mt-3">
              <table className="table table-hover">
                <thead>
                  <tr className="text-muted small">
                    <th>Employee</th>
                    <th>Code</th>
                    <th>Department</th>
                    <th>Shift</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {dataLoading ? (
                    <tr>
                      <td colSpan="5" className="text-center py-3">
                        Loading...
                      </td>
                    </tr>
                  ) : filtered.length ? (
                    filtered.map((emp) => (
                      <tr key={emp.id}>
                        <td>
                          <strong>
                            {emp.first_name} {emp.last_name}
                          </strong>
                          <div className="text-muted small">{emp.email}</div>
                        </td>

                        <td>{emp.employee_code || "-"}</td>
                        <td>{emp.department || "-"}</td>
                        <td>
                          {emp.shift_start_time
                            ? `${formatTime(
                                emp.shift_start_time
                              )} - ${formatTime(emp.shift_end_time)}`
                            : "-"}
                        </td>

                        <td>
                          <div className="dropdown">
                            <MoreVertical
                              size={16}
                              className="dropdown-toggle"
                              data-bs-toggle="dropdown"
                            />
                            <ul className="dropdown-menu">
                              <li>
                                <div className="dropdown-item">
                                  <Edit size={16} /> Edit
                                </div>
                              </li>

                              <li>
                                <div
                                  className="dropdown-item text-danger"
                                  onClick={() => handleRemove(emp.id)}
                                >
                                  <Trash2 size={16} /> Remove
                                </div>
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-3">
                        No employees found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* UPLOAD TAB */}
      {activeTab === "upload" && <EmployeeUpload />}
    </div>
  );
};

export default AddNewEmployee;
